/**
 * Model API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound ModelApi.
 * Each tool maps to a method on the C# ModelApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface ModelApiToolSpec {
  /** Method name on the JS-side modelApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
  /**
   * Optional map of arg-name → transform applied before calling CefSharp.
   * Use JSON.stringify for arguments the C# side expects as a JSON string.
   */
  argTransforms?: Record<string, (v: unknown) => unknown>;
}

const TOOL_SPECS: ModelApiToolSpec[] = [
  // ── Object counts ─────────────────────────────────────────────────────
  {
    method: "getModelObjectCount",
    name: "WS-modelApi-getModelObjectCount",
    description:
      "Returns a JSON object with the total number of features and net elements " +
      "in the current Quadri model. Fields: featureCount, netElementCount.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Extent & Area of Interest ─────────────────────────────────────────
  {
    method: "getModelExtent",
    name: "WS-modelApi-getModelExtent",
    description:
      "Returns the bounding box of the entire model as a JSON object with " +
      "minPoint {x, y, z} and maxPoint {x, y, z}.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
  {
    method: "getAreaOfInterest",
    name: "WS-modelApi-getAreaOfInterest",
    description:
      "Returns the area-of-interest polygon as a JSON object with a vertices " +
      "array of {x, y} points.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
  {
    method: "getObjectsOutsideAreaOfInterest",
    name: "WS-modelApi-getObjectsOutsideAreaOfInterest",
    description:
      "Returns a JSON object with totalCount, insideAreaOfInterest, and " +
      "outsideAreaOfInterest feature counts relative to the area of interest polygon.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Collection tasks ──────────────────────────────────────────────────
  {
    method: "getCollectionTasks",
    name: "WS-modelApi-getCollectionTasks",
    description:
      "Returns a JSON array of collection tasks (sampling) in the model. " +
      "Each entry includes guid, name, description, folderPath, featureCount, and netElementCount.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Open in view ──────────────────────────────────────────────────────
  {
    method: "openInView",
    name: "WS-modelApi-openInView",
    description:
      "Opens the entire model or a specific task result in a 2D or 3D view window. " +
      'viewType must be "2D" or "3D". taskGuid is optional — omit it or pass "model" ' +
      "to open the full model. Pass a task GUID string to open that task's result. " +
      'Returns "true" on success.',
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description:
            'Optional task GUID to open. Omit or pass "model" to view the entire model.',
        },
        viewType: {
          type: "string",
          description: 'The view type: "2D" or "3D".',
        },
      },
      required: ["viewType"],
    },
    argOrder: ["taskGuid", "viewType"],
  },
  {
    method: "openTasksInView",
    name: "WS-modelApi-openTasksInView",
    description:
      "Opens multiple task results in a single 2D or 3D view window. " +
      'viewType must be "2D" or "3D". taskGuids is an array of task GUID strings ' +
      "(from WS-modelApi-getCollectionTasks or WS-modelApi-getTasks). " +
      "Returns a JSON object with success status and the number of tasks opened.",
    parameters: {
      type: "object",
      properties: {
        taskGuids: {
          type: "array",
          items: { type: "string" },
          description: "Array of task GUID strings to open in the view.",
        },
        viewType: {
          type: "string",
          description: 'The view type: "2D" or "3D".',
        },
      },
      required: ["taskGuids", "viewType"],
    },
    argOrder: ["taskGuids", "viewType"],
    argTransforms: { taskGuids: (v: unknown) => JSON.stringify(v) },
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
  argTransforms?: Record<string, (v: unknown) => unknown>,
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const modelApi = api?.modelApi;
      if (!modelApi) {
        return JSON.stringify({
          success: false,
          error:
            "ModelApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = modelApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: modelApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => {
        const val = args?.[name];
        return argTransforms?.[name] ? argTransforms[name](val) : val;
      });
      const raw = await invokeCefSharpMethod<string>(
        "modelApi",
        modelApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every ModelApi method.
 * Call this after `CefSharp.BindObjectAsync("modelApi")` has resolved.
 */
export function createModelApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(
        spec.method,
        spec.argOrder,
        spec.argTransforms,
      ),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
